import { Group, Text } from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
	createFacetedRowModel,
	createFacetedUniqueValues,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { DownloadIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import { FormattedPrice } from '@apple/utils/globalization/components/FormattedPrice';
import type { CurrencyCode } from '@apple/utils/globalization/models';
import { globalizationQueryOptions } from '@apple/utils/globalization/queries';

import { calendarQueryOptions } from '@apple/features/calendar/queries';
import { plantQueryOptions } from '@apple/features/plants/queries/queries';
import { getBudgetReportUrl } from '@apple/features/store/api/management';
import type { BudgetFilters, BudgetTableData } from '@apple/features/store/models/budget';
import { storeQueries } from '@apple/features/store/queries';
import { budgetsOdataQueryOptions } from '@apple/features/store/queries/odata';

const features = createDataTableFeatures({
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
});

export const Route = createFileRoute('/_authed/reports/budgets')({
	beforeLoad: async args => {
		const data = await args.context.queryClient.fetchQuery(calendarQueryOptions.current);

		return {
			currentPeriod: data,
		};
	},
	component: BudgetsRoute,
});

export function BudgetsRoute() {
	const { t } = useTranslation('store');
	const navigate = Route.useNavigate();
	const search = Route.useSearch();

	const excelTemplate = useFileDownload({
		method: 'post',
		url: getBudgetReportUrl(),
		fallbackFilename: 'storeBudget.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});

	const { currentPeriod } = Route.useRouteContext();

	const tableState = useTableState<BudgetTableData, BudgetFilters>({
		search,
		navigate,
		defaultFilters: {
			periodNumber: currentPeriod.periodNumber,
			fiscalYear: currentPeriod.fiscalYear,
		},
		defaultSorting: [{ id: 'remainingValue', desc: false }],
		fieldMap: [],
	});

	const [
		plantsQuery,
		storesQuery,
		countriesQuery,
		budgetSearchQuery,
		calendarQuery,
		periodsQuery,
	] = useQueries({
		queries: [
			plantQueryOptions.plants,
			storeQueries.accessible(),
			globalizationQueryOptions.countries,
			budgetsOdataQueryOptions({
				pagination: tableState.state.pagination,
				filters: tableState.currentFilterData,
				sorting: tableState.state.sorting,
			}),
			calendarQueryOptions.list,
			calendarQueryOptions.fiscalYear(
				tableState.currentFilterData.fiscalYear ?? currentPeriod.fiscalYear,
			),
		],
	});

	const plantCodes = useMemo(
		() => plantsQuery.data?.map(x => x.warehouseCode) ?? [],
		[plantsQuery.data],
	);

	const columnHelper = createDataTableColumnHelper<BudgetTableData, BudgetFilters>();

	const table = useTable({
		features,
		data: budgetSearchQuery.data.rows,
		rowCount: budgetSearchQuery.data.totalRowCount,
		...tableState,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		columnResizeMode: 'onChange',
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'fiscalYear':
						return new Map(calendarQuery.data?.map(x => [x.fiscalYear, 1]) ?? []);
					case 'periodNumber': {
						const emptyValue = [-1, 1] satisfies [number, number];
						if (
							tableState.currentFilterData.fiscalYear === undefined ||
							periodsQuery.data === undefined
						) {
							return new Map([emptyValue]);
						}

						const entries: [number, number][] = [
							emptyValue,
							...periodsQuery.data.fiscalPeriods.map(
								p => [p.periodNumber, 1] satisfies [number, number],
							),
						];

						return new Map(entries);
					}
					case 'isOverBudget':
						return new Map([
							[undefined, 1],
							[true, 1],
							[false, 1],
						]);
					case 'countryCode':
						return new Map(countriesQuery.data?.map(x => [x.key, 1]));
					case 'warehouseCode':
						return new Map(plantCodes.map(x => [x, 1]));
					case 'storeIds':
						return new Map(storesQuery.data?.map(x => [x.thirdPartyCustomerId, 1]));
					default:
						return undefined;
				}
			},
		},
		columns: columnHelper.columns([
			columnHelper.accessor('storeId', {
				size: 200,
				enableColumnFilter: false,
				header: t('budgets.listing.fields.store'),
				cell: ({ row }) => {
					const store = `${row.original.storeId} - ${row.original.storeName}`;

					return calendarQuery.isSuccess ? (
						<Link
							to='/stores/$storeCode/budget'
							params={{ storeCode: row.original.storeCode }}
							search={{
								fiscalYear: row.original.fiscalYear,
								fiscalPeriod: row.original.periodNumber,
							}}
							title={t('budgets.listing.title')}
						>
							<Text size='sm'>{store}</Text>
						</Link>
					) : (
						<Text size='sm'>{store}</Text>
					);
				},
			}),
			columnHelper.filterOnly('storeIds', {
				group: t('budgets.listing.detailFilterGroup'),
				label: t('budgets.listing.fields.store'),
				variant: 'multi-select',
				getFilterDisplayValue: value =>
					storesQuery.data?.find(x => x.thirdPartyCustomerId === value)?.displayString ??
					String(value),
			}),

			columnHelper.filterOnly('warehouseCode', {
				group: t('budgets.listing.detailFilterGroup'),
				label: t('budgets.listing.fields.plant'),
				variant: 'select',
				getFilterDisplayValue: value =>
					plantsQuery.data?.find(x => x.warehouseCode === value)?.name ?? String(value),
			}),
			columnHelper.accessor('countryCode', {
				size: 30,
				enableColumnFilter: true,
				header: t('budgets.listing.fields.country'),
				filter: {
					group: t('budgets.listing.detailFilterGroup'),
					variant: 'select',
					getFilterDisplayValue: value =>
						countriesQuery.data?.find(x => x.key === value)?.value ?? String(value),
				},
			}),
			columnHelper.accessor('fiscalYear', {
				size: 30,
				enableColumnFilter: true,
				header: t('budgets.listing.fields.year'),
				cell: ({ row }) => {
					return row.original.fiscalYear;
				},
				filter: {
					group: t('budgets.listing.detailFilterGroup'),
					variant: 'select',
					label: t('budgets.listing.fields.year'),
					getFilterDisplayValue: value => String(value),
				},
			}),
			columnHelper.accessor('periodName', {
				size: 30,
				enableColumnFilter: false,
				header: t('budgets.listing.fields.month'),
			}),
			columnHelper.filterOnly('periodNumber', {
				group: t('budgets.listing.detailFilterGroup'),
				label: t('budgets.listing.fields.month'),
				variant: 'select',
				getFilterDisplayValue: value =>
					value === -1
						? 'All'
						: (periodsQuery.data?.fiscalPeriods.find(p => p.periodNumber === value)
								?.periodName ?? String(value)),
				sortCompareFn: (a, b) => {
					return Number(b) - Number(a);
				},
			}),
			columnHelper.filterOnly('isOverBudget', {
				group: t('budgets.listing.detailFilterGroup'),
				label: t('budgets.listing.fields.overBudget'),
				variant: 'segmented-switch',
				getFilterDisplayValue: value => {
					switch (value) {
						case true:
							return 'Yes';
						case false:
							return 'No';
						default:
							return 'All';
					}
				},
			}),
			columnHelper.accessor('allocatedAmount', {
				enableColumnFilter: false,
				header: t('budgets.listing.fields.budget'),
				cell: ({ row }) => {
					const value = row.original.allocatedAmount;
					return (
						<FormattedPrice
							value={value}
							currency={row.original.currencyCode as CurrencyCode}
							hideCurrencyCode={true}
							currencyDisplay='narrowSymbol'
						/>
					);
				},
			}),
			columnHelper.accessor('transactionTotal', {
				enableColumnFilter: false,
				header: t('budgets.listing.fields.spent'),
				cell: ({ row }) => {
					return (
						<FormattedPrice
							value={row.original.transactionTotal}
							currency={row.original.currencyCode as CurrencyCode}
							hideCurrencyCode={true}
							currencyDisplay='narrowSymbol'
						/>
					);
				},
			}),
			columnHelper.accessor('pendingTotal', {
				enableColumnFilter: false,
				header: t('budgets.listing.fields.pending'),
				cell: ({ row }) => {
					return (
						<FormattedPrice
							value={row.original.pendingTotal}
							currency={row.original.currencyCode as CurrencyCode}
							hideCurrencyCode={true}
							currencyDisplay='narrowSymbol'
						/>
					);
				},
			}),
			columnHelper.accessor('remainingValue', {
				enableColumnFilter: false,
				header: t('budgets.listing.fields.remaining'),
				cell: ({ row }) => {
					const rawValue = row.original.remainingValue;
					const isNegative = rawValue < 0;

					return (
						<Group style={{ color: isNegative ? 'red' : 'var(--mantine-color-text)' }}>
							<FormattedPrice
								value={Math.abs(rawValue)}
								currency={row.original.currencyCode as CurrencyCode}
								hideCurrencyCode={true}
								currencyDisplay='narrowSymbol'
							/>
						</Group>
					);
				},
			}),
		]),
	});

	return (
		<TableLayout
			title={t('budgets.listing.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='download'
					tooltip={t('budgets.listing.tooltip.title')}
					icon={DownloadIcon}
					loading={excelTemplate.downloading}
					onClick={() => void excelTemplate.download()}
				/>,
			]}
		>
			<DataTable table={table} loading={budgetSearchQuery.isFetching} />
		</TableLayout>
	);
}
